import React, { useEffect } from "react";
import {
  Page,
  Text,
  View,
  Document,
  StyleSheet,
  Image,
  Font,
  Link
} from "@react-pdf/renderer";
import { Spin, Table } from "antd";
import ROWHWG_LOGO from "../../img/ROWHWG_logo.png";
import { useSelector, useDispatch } from "react-redux";
import ccaa_logo from "../../img/ccaa_logo_white.png";

// Register the "Oswald" font
Font.register({
  family: "Oswald",
  src: "https://fonts.gstatic.com/s/oswald/v13/Y_TKV6o8WovbUd3m_X9aAA.ttf"
});

// Create styles
const styles = StyleSheet.create({
  body: {
    paddingTop: 35,
    paddingBottom: 65,
    paddingHorizontal: 35
  },
  title: {
    fontSize: 24,
    textAlign: "center",
    fontFamily: "Oswald"
  },
  author: {
    fontSize: 12,
    textAlign: "center",
    marginBottom: 40
  },
  subtitle: {
    fontSize: 18,
    margin: 12,
    fontFamily: "Oswald",
    textDecoration: "underline",
    textAlign: "center"
  },
  text: {
    margin: 12,
    fontSize: 14,
    textAlign: "justify",
    fontFamily: "Times-Roman"
  },
  image: {
    height: 125,
    width: 300,
    marginLeft: "auto",
    marginRight: "auto"
  },
  header: {
    fontSize: 12,
    marginBottom: 20,
    textAlign: "center",
    color: "grey"
  },
  pageNumber: {
    position: "absolute",
    fontSize: 12,
    bottom: 30,
    left: 0,
    right: 0,
    textAlign: "center",
    color: "grey"
  },
  disclaimer: {
    fontSize: 12,
    margin: 12,
    textAlign: "justify",
    fontFamily: "Times-Roman"
  },
  table: {
    width: "100%",
    border: "1px solid #000",
    borderCollapse: "collapse",
    margin: 10,
    marginBottom: 20,
    fontSize: 8,
    fontFamily: "Times-Roman",
    textAlign: "center",
    display: "table",
    borderWidth: 1,
    borderColor: "#000",
    borderStyle: "solid",
    borderLeft: 0,
    borderTop: 0
  },

  // Styles for table rows
  tableRow: {
    flexDirection: "row",
    borderBottom: "1px solid #000" // Add this line for the border
  },

  // Styles for table header cells
  tableHeaderCell: {
    flex: 1,
    backgroundColor: "#f2f2f2",
    padding: 5,
    border: "1px solid #000",
    overflowWrap: "break-word",
    wordWrap: "break-word",
    hyphens: "auto"
  },

  // Styles for table data cells
  tableCell: {
    flex: 1,
    padding: 5,
    border: "1px solid #000",
    overflowWrap: "break-word",
    wordWrap: "break-word",
    hyphens: "auto"
  },

  tableCellText: {
    fontSize: 8,
    fontFamily: "Times-Roman",
    overflowWrap: "break-word",
    wordWrap: "break-word",
    hyphens: "auto"
  },

  // Styles for table header cell text
  tableCellHeader: {
    fontSize: 8,
    fontFamily: "Times-Roman",
    fontWeight: "bold",
    overflowWrap: "break-word",
    wordWrap: "break-word",
    hyphens: "auto"
  }
});

function MonarchResultsPdf(props) {
  //console.log('props in monarch results pdf', props);
  let home_page_store = props.home_page_store;
  let annual_reporting_summary_store = props.annual_reporting_summary_store;
  let annual_net_conservation_store = props.annual_net_conservation_store;
  let summary_of_effectiveness_store = props.summary_of_effectiveness_store;
  let upcoming_year_forecast_store = props.upcoming_year_forecast_store;
  let organization_name = props.organization_name;

  // Define a function to create a table-like structure
  const renderTable = (data, columns) => (
    <View>
      {/* Table Header */}
      <View style={styles.tableRow}>
        {columns.map((column, index) => (
          <View key={index} style={styles.tableHeaderCell}>
            <Text style={styles.tableCellHeader}>{column.title}</Text>
          </View>
        ))}
      </View>

      {/* Table Rows */}
      {data.map((row, rowIndex) => (
        <View key={rowIndex} style={styles.tableRow}>
          {columns.map((column, colIndex) => (
            <View key={colIndex} style={styles.tableCell}>
              <Text style={styles.tableCellText}>{row[column.dataIndex]?row[column.dataIndex]:'N/A'}</Text>
            </View>
          ))}
        </View>
      ))}
    </View>
  );

  return (
    <>
      <Document>
        <Page style={styles.body} wrap={false}>
          <Text style={styles.header} fixed>
            ~ Results Created on{" "}
            {new Date().toLocaleString("en-US").split(",")[0]} ~
          </Text>
          <Image style={styles.image} src={ccaa_logo} />
          <Text style={styles.title}>2024 Annual Report</Text>
          <Text style={styles.subtitle}>Partner Information</Text>
          {renderTable(
            [
              {
                "Organization Name": organization_name,
                "Inclusion Number": home_page_store.inclusion_number,
                "Contact Name": home_page_store.contact_name,
                Address: home_page_store.address,
                "Phone Number": home_page_store.phone_number,
                Email: home_page_store.email
              }
            ],
            [
              {
                title: "Organization Name",
                dataIndex: "Organization Name"
              },
              {
                title: "Inclusion Number",
                dataIndex: "Inclusion Number"
              },
              {
                title: "Contact Name",
                dataIndex: "Contact Name"
              },
              {
                title: "Address",
                dataIndex: "Address"
              },
              {
                title: "Phone Number",
                dataIndex: "Phone Number"
              },
              {
                title: "Email",
                dataIndex: "Email"
              }
            ]
          )}

          <Text style={styles.subtitle}>Annual Report Summary</Text>
          {renderTable(
            [
              {
                "Acres Enrolled": annual_reporting_summary_store.acres_enrolled,
                "Acres Target": annual_reporting_summary_store.acres_target,
                "Acres Contributed":
                  annual_reporting_summary_store.acres_contributed,
                "Additional Explanation":
                  annual_reporting_summary_store.additional_explanation
              }
            ],
            [
              {
                title: "Acres Enrolled",
                dataIndex: "Acres Enrolled"
              },
              {
                title: "Acres Target",
                dataIndex: "Acres Target"
              },
              {
                title: "Acres Contributed",
                dataIndex: "Acres Contributed"
              },
              {
                title: "Additional Explanation",
                dataIndex: "Additional Explanation"
              }
            ]
          )}
          <Text style={styles.subtitle}>Annual Net Conservation</Text>
          {renderTable(
            [
              {
                "Conservation Measures":
                  annual_net_conservation_store.conservation_measures.length>1?annual_net_conservation_store.conservation_measures.join(", "):annual_net_conservation_store.conservation_measures,
                "Conservation Measures Implementation":
                  annual_net_conservation_store.conservation_measures_implementation,
                "Partners Overlap":
                  annual_net_conservation_store.partners_overlap,
                "Supplement Measures":
                  annual_net_conservation_store.supplement_measures.length>1?annual_net_conservation_store.supplement_measures.join(", "):annual_net_conservation_store.supplement_measures,
                "Additional Supplement Conservation Measures":
                  annual_net_conservation_store.additional_supplement_conservation_measures,
                "Updates to Section 7":
                  annual_net_conservation_store.updates_to_section_7
              }
            ],
            [
              {
                title: "Conservation Measures",
                dataIndex: "Conservation Measures"
              },
              {
                title: "Conservation Measures Implementation",
                dataIndex: "Conservation Measures Implementation"
              },
              {
                title: "Partners Overlap",
                dataIndex: "Partners Overlap"
              },
              {
                title: "Supplement Measures",
                dataIndex: "Supplement Measures"
              },
              {
                title: "Additional Supplement Conservation Measures",
                dataIndex: "Additional Supplement Conservation Measures"
              },
              {
                title: "Updates to Section 7",
                dataIndex: "Updates to Section 7"
              }
            ]
          )}
          <Text style={styles.subtitle}>Summary of Effectiveness</Text>
          {renderTable(
            [
              {
                "Methods Effective Monitoring":
                  summary_of_effectiveness_store.methods_effective_monitoring,
                "Monitoring Results Summary":
                  summary_of_effectiveness_store.monitoring_results_summary,
                "Adaptive Management Triggered":
                  summary_of_effectiveness_store.adaptive_management_triggered,
                "Adaptive Management Actions":
                  summary_of_effectiveness_store.adaptive_management_actions,
                "Supplemental Effectiveness Monitoring":
                  summary_of_effectiveness_store.supplemental_effectiveness_monitoring
              }
            ],
            [
              {
                title: "Methods Effective Monitoring",
                dataIndex: "Methods Effective Monitoring"
              },
              {
                title: "Monitoring Results Summary",
                dataIndex: "Monitoring Results Summary"
              },
              {
                title: "Adaptive Management Triggered",
                dataIndex: "Adaptive Management Triggered"
              },
              {
                title: "Adaptive Management Actions",
                dataIndex: "Adaptive Management Actions"
              },
              {
                title: "Supplemental Effectiveness Monitoring",
                dataIndex: "Supplemental Effectiveness Monitoring"
              }
            ]
          )}
          <Text style={styles.subtitle}>Upcoming Year Forecast</Text>
          {renderTable(
            [
              {
                "Anticipate Updates":
                  upcoming_year_forecast_store.anticipate_updates,
                "General Comments":
                  upcoming_year_forecast_store.general_comments,
                  "Suggesstions for CCAA":
                  upcoming_year_forecast_store.suggestions_for_ccaa
              }
            ],
            [
              {
                title: "Anticipate Updates",
                dataIndex: "Anticipate Updates"
              },
              {
                title: "General Comments",
                dataIndex: "General Comments"
              }
            ]
          )}
        </Page>
      </Document>
    </>
  );
}

export default MonarchResultsPdf;
